import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import classNames from 'classnames';
import _ from 'lodash';

import './NextRaces.scss';
import { RootState, Dispatch } from 'src/configs/store';
import { getFlag } from 'src/utils/utils';
import RacingDetailContext, { RacingDetailContextProps } from 'src/components/HorseBetting/Context/RacingDetail.context';
import Page from 'src/containers/Page/Page';
import Icon from 'src/components/Icon/Icon';
import Accordion from 'src/components/Accordion/Accordion';
import Button from 'src/components/Button/Button';
import CompetitorsTable from 'src/components/HorseBetting/CompetitorsTable/CompetitorsTable';
import { Helmet } from 'react-helmet';

export default function NextRaces() {
  const [openAll, setOpenAll] = useState(false);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [order, setOrder] = useState<RacingDetailContextProps['order']>('asc');
  const [orderBy, setOrderBy] = useState('startNo');

  const { t } = useTranslation('common');
  const history = useHistory();

  const { nextRaces } = useSelector((state: RootState) => state.events);
  const { selectedTimezone } = useSelector((state: RootState) => state.odds);
  const { optionsType } = useSelector((state: RootState) => state.filters);
  const dispatch = useDispatch<Dispatch>();

  const context = {
    selectedMarket,
    setSelectedMarket,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    hideDetail: true
  };

  const calculateTournaments = () => {
    let tournaments = [];

    if (nextRaces?.length > 0) {
      tournaments = _.filter(nextRaces, tournament => {
        if ('event' in tournament) {
          if (optionsType) {
            if ((tournament.eventType === 202 || tournament.eventType === 201) && optionsType.horses === true) {
              return true;
            } else if (tournament.eventType === 203 && optionsType.carriages === true) {
              return true;
            } else if (tournament.eventType === 204 && optionsType.dogs === true) {
              return true;
            } else {
              return false;
            }
          }

          return true;
        } else {
          return false;
        }
      });
    }

    return tournaments;
  };

  const renderAccordionTitle = (tournament) => {
    const date = utcToZonedTime(tournament.eventStart * 1000, selectedTimezone);
    let type = '';
    if (tournament.eventType === 201 || tournament.eventType === 202) {
      type = 'horses';
    } else if (tournament.eventType === 204) {
      type = 'dogs';
    } else if (tournament.eventType === 203) {
      type = 'carriages';
    }

    return (
      <>
        <li
          className={
            classNames('next-draws__item', {
              'next-draws__item--horse': type === 'horses',
              'next-draws__item--dog': type === 'dogs',
              'next-draws__item--carriage': type === 'carriages'
            })
          }
        >
          <span className="next-draws__flag" style={ { backgroundImage: `url(${ getFlag(tournament.countryCode) })` } }></span>
          <span className="next-draws__name">{ tournament.name }</span>
          <div className="next-draws__info">
            <span className="next-draws__icon--time">{ format(date, 'dd MMM HH:mm') }</span>
          </div>
          {
            tournament.meetingInfos && <div className="next-draws__info--sum">
              <span className="next-draws__info--sum">
                { `${ tournament.event?.raceInfos.no } / ${ tournament.meetingInfos.noRaces }` }
              </span>
            </div>
          }
        </li>
        {
          type !== '' && (
            <Icon
              icon={ type }
              className={ `next-races__accordion-icon next-races__accordion-icon--${ type }` }
            />
          )
        }
      </>
    );
  };

  useEffect(() => {
    dispatch.racing.getNextRaces(false);

    const refreshInterval = setInterval(() => {
      dispatch.racing.getNextRaces(true);
    }, 120000);

    return () => {
      clearInterval(refreshInterval);
    };
  }, []);

  useEffect(() => {
    if (nextRaces) {
      for (const tournament of nextRaces) {
        if ('event' in tournament) {
          const foMarkets = _.filter(tournament.event.markets, { oddsType: 3 });

          if (foMarkets.length > 0) {
            setSelectedMarket(foMarkets[2] || foMarkets[1] || foMarkets[0]);
          }
        }
      }
    }

  }, [nextRaces]);

  const getRaceType = (tournament) => {
    if (tournament.eventType === 201 || tournament.eventType === 202) {
      return 'horses';
    } else if (tournament.eventType === 204) {
      return 'dogs';
    } else if (tournament.eventType === 203) {
      return 'carriages';
    } else {
      return '';
    }
  };

  return (
    <Page>
      <Helmet>
        <title>BetXchange - Next Races</title>
      </Helmet>
      <div className="next-races">
        <div className="next-races__header">
          <Link to="/racing" className="next-races__back">
            <Icon icon="arrow" className="next-races__arrow" />
            <span>{ t('racingDetail.back') }</span>
          </Link>
          <div className="next-races__title">{ t('nextRaces.title') }</div>
        </div>
        <Button
          className="racing-detail__open-all"
          onClick={ () => setOpenAll(!openAll) }
        >
          { !openAll ? t('racingDetail.openAll') : t('racingDetail.closeAll') }
        </Button>
        {
          _.map(calculateTournaments(), (tournament) => (
            <div
              key={ `${ tournament.name }${ tournament.eventId }` }
              className={
                classNames({
                  'next-races__accordion-box--horses': getRaceType(tournament) === 'horses',
                  'next-races__accordion-box--dogs': getRaceType(tournament) === 'dogs',
                  'next-races__accordion-box--carriages': getRaceType(tournament) === 'carriages'
                })
              }
            >
              <Accordion
                key={ `${ tournament.name }_${ tournament.event?.raceInfos.no }/${ tournament.meetingInfos.noRaces }` }
                title={ renderAccordionTitle(tournament) }
                open={ openAll }
                totalMarkets ={ tournament.event.marketCount }
                onClickMarkets={ () => history.push(`/racing/${ tournament.meetingInfos.meetingCode }/${ tournament.eventId }`) }
              >
                <Link to={ `/racing/${ tournament.meetingInfos.meetingCode }/${ tournament.eventId }` } className="next-races__race">
                  {
                    t('nextRaces.race', {
                      raceNo: tournament.event?.raceInfos.no,
                      time: format(utcToZonedTime(tournament.eventStart * 1000, selectedTimezone), 'HH:mm')
                    })
                  }
                </Link>

                {
                  tournament.event && (
                    <RacingDetailContext.Provider value={ { ...context, event: tournament.event } }>
                      <CompetitorsTable
                        selectedEvent={
                          {
                            ...tournament.event,
                            eventCode: tournament.event.code,
                            name: tournament.name,
                            eventStart: tournament.eventStart,
                            countryCode: tournament.countryCode,
                            raceNo: tournament.meetingInfos.nextRaceNo
                          }
                        }
                        maxMarkets={ 3 }
                        quickCharts
                        racingType={ getRaceType(tournament) }
                      />
                    </RacingDetailContext.Provider>
                  )
                }
              </Accordion>
            </div>
          ))
        }
      </div>
    </Page>
  );
}
